import classnames from 'classnames';
import { map } from 'ramda';
import { isFunction } from 'ramda-adjunct';
import React, { CSSProperties, FC, useCallback, useEffect, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FormattedMessage } from 'react-intl';
import { Route, useHistory } from 'react-router';
import lassoSelectedAreaStyles from '../../common/Lasso/withLassoSelectedAreaStyle/LassoSelectedArea.scss';
import { RoutePath } from '../../../constants/Routes';
import { MainSource } from '../../../constants/Source';
import { useTrackSearchAndFiltersAnalytics } from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics';
import {
  ClickOrigins,
  SearchAndFiltersShowAllTargets,
} from '../../../utils/hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';
import { useSelectAndScrollToFileOnMount } from '../../../utils/hooks/useSelectAndScrollToFileOnMount';
import { RouteBuilder } from '../../../utils/RouteHelpers';
import { Tag, Text } from '../../common';
import { IInner, IOuter } from '../types';
import styles from './GenericLayout.scss';
import { useMMGRSettings } from '../../../utils/hoc/withMMGRSettings';

const infiniteStyle: CSSProperties = { position: 'relative', overflowX: 'hidden', flexDirection: 'column' };

export const GenericLayoutComponent: FC<IOuter & IInner> = ({
  className,
  itemsClassName,
  hasMoreItems,
  children,
  onClick,
  onContextMenu,
  loadMoreItems,
  dataHook = 'generic-layout',
  itemsCount,
  extendedFiltersElement,
  selectedAreaStyle,
  saveScrollingElementWrapperRef,
  saveRef,
  stickyThreshold = 0,
  selectItem,
  saveScrollElementNodeForItemsLoader,
  searchQuery,
  otherCategoriesWithResults = [],
  handleCategoryTagClick,
}) => {
  const handleInfiniteScrollRef = useCallback(
    (ScrollingComponentRef) => {
      saveScrollElementNodeForItemsLoader(ScrollingComponentRef);

      if (ScrollingComponentRef && saveScrollingElementWrapperRef) {
        saveScrollingElementWrapperRef(ScrollingComponentRef.el);
      }
    },
    [saveScrollElementNodeForItemsLoader, saveScrollingElementWrapperRef],
  );

  const ulRef = useRef(null);

  useSelectAndScrollToFileOnMount(selectItem);

  useEffect(() => {
    if (isFunction(saveRef)) {
      saveRef(ulRef.current);
    }
  }, [saveRef]);

  const history = useHistory();
  const { trackSearchAndFiltersShowAllClick } = useTrackSearchAndFiltersAnalytics();

  const handleLinkClick = useCallback(
    (_, event) => {
      event.stopPropagation();

      history.push(RouteBuilder.Search(MainSource.All, searchQuery));

      trackSearchAndFiltersShowAllClick({
        target: SearchAndFiltersShowAllTargets.AllResults,
        clickOrigin: ClickOrigins.Search,
      });
    },
    [history, searchQuery, trackSearchAndFiltersShowAllClick],
  );

  const otherCategoriesTags = map(
    ({ id, name }) => (
      <Tag
        id={id}
        key={id}
        removable={false}
        size="small"
        className={styles.quickSearchTag}
        onClick={handleCategoryTagClick}
        dataHook="quick-search-category"
      >
        {name}
      </Tag>
    ),
    otherCategoriesWithResults,
  );

  return (
    <div className={className} onClick={onClick} data-hook={dataHook}>
      <InfiniteScroll
        scrollThreshold="400px"
        dataLength={itemsCount}
        next={loadMoreItems}
        hasMore={hasMoreItems}
        height="100%"
        ref={handleInfiniteScrollRef}
        style={infiniteStyle}
        loader={null}
      >
        {extendedFiltersElement && <>{extendedFiltersElement}</>}
        <ul
          ref={ulRef}
          className={classnames(styles.itemsList, itemsClassName)}
          onContextMenu={onContextMenu}
          data-hook="generic-layout-items-list-wrapper"
        >
          {children}
        </ul>

        {selectedAreaStyle && (
          <div className={lassoSelectedAreaStyles.selectedArea} style={selectedAreaStyle} data-element-type="lasso" />
        )}
      </InfiniteScroll>
    </div>
  );
};
